import { DesktopCloudClient } from "@repo/shared/desktopCloud/client";
import type { DesktopCloudPlatform } from "@repo/shared/desktopCloud/types";
import { isWebRuntime } from "../lib/runtime";
import { createBrowserCloudPlatform } from "./browserCloudPlatform";

function hasElectronPreload(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as {
    desktopV2Api?: unknown;
    __JUSTWRITE_RUNTIME__?: "electron" | "web";
  };
  if (w.__JUSTWRITE_RUNTIME__ === "web") return false;
  if (w.__JUSTWRITE_RUNTIME__ === "electron") return true;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /\bElectron\//.test(ua) && typeof w.desktopV2Api !== "undefined";
}

function createElectronCloudPlatform(): DesktopCloudPlatform {
  return {
    getBaseUrl: () => window.desktopV2Api.getCloudWebBaseUrl(),
    openExternalUrl: (url) => window.desktopV2Api.openExternalUrl(url),
    getRefreshToken: () => window.desktopV2Api.cloudRefreshGet(),
    setRefreshToken: async (token) => {
      await window.desktopV2Api.cloudRefreshSet(token);
    },
    httpRequest: (request) => window.desktopV2Api.cloudHttpRequest(request),
  };
}

/**
 * Resolve platform at call time so HMR / import order cannot lock Electron IPC
 * before `installBrowserDesktopV2Api` sets `__JUSTWRITE_RUNTIME__ = "web"`.
 */
function resolvePlatform(): DesktopCloudPlatform {
  return hasElectronPreload() ? createElectronCloudPlatform() : createBrowserCloudPlatform();
}

const lazyPlatform: DesktopCloudPlatform = {
  getBaseUrl: () => resolvePlatform().getBaseUrl(),
  openExternalUrl: (url) => resolvePlatform().openExternalUrl(url),
  getRefreshToken: () => resolvePlatform().getRefreshToken(),
  setRefreshToken: (token) => resolvePlatform().setRefreshToken(token),
  httpRequest: (request) => resolvePlatform().httpRequest(request),
};

export const cloudAuthDesktopV2 = new DesktopCloudClient(lazyPlatform);

/**
 * Electron uses device pairing; the browser SPA uses the shared NextAuth session
 * on heyjustwrite.com (no pairing code).
 */
export async function signInForCurrentRuntime(): Promise<void> {
  if (isWebRuntime()) {
    await cloudAuthDesktopV2.signInWithSpaSession({ explicit: true });
    return;
  }
  await cloudAuthDesktopV2.signInWithWebFlow();
}
